import React, { useEffect, useRef, useState } from 'react';

type Option = {
  label: string;
  value: string;
};

type MultiSelectProps = {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  readOnly?: boolean;
};

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  error,
  helperText,
  readOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleOption = (option: Option) => {
    if (value.find(v => v === option.value)) {
      onChange(value.filter(v => v !== option.value));
    } else {
      onChange([...value, option.value]);
    }
  };

  const removeSelected = (option: string) => {
    onChange(value.filter(v => v !== option));
  };

  const isSelected = (option: Option) => !!value.find(selected => selected === option.value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="w-full" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map(opt => (
          <span
            key={opt}
            className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm"
          >
            {options.find(o => o.value === opt)?.label}
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeSelected(opt)}
                className="ml-1 text-indigo-500 hover:text-indigo-700"
              >
                ✕
              </button>
            )}
          </span>
        ))}
        {value.length === 0 && <p className="text-sm text-gray-400">{placeholder}</p>}
      </div>

      {/* Dropdown Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={readOnly}
          className={`w-full px-3 py-2 border ${
            error ? 'border-red-500' : 'border-gray-300'
          } bg-white rounded-md shadow-sm text-left text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
        >
          {value.length === 0 ? 'Choose options' : `Selected ${value.length} option(s)`}
          <span className="float-right">{isOpen ? '▲' : '▼'}</span>
        </button>

        {/* Dropdown List */}
        {isOpen && !readOnly && (
          <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg">
            {options.map(option => (
              <li
                key={option.value}
                onClick={() => toggleOption(option)}
                className={`cursor-pointer px-4 py-2 text-sm hover:bg-indigo-50 ${
                  isSelected(option) ? 'bg-indigo-100 text-indigo-700 font-semibold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Error / Helper Text */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
    </div>
  );
};

export default MultiSelect;
